/**
 * Central tier/admin utility helpers for the frontend.
 *
 * Single source of truth for all tier-dependent limits and feature gates.
 * Backend equivalent: Shared/common/src/utils/tier-utils.ts
 *
 * Tier hierarchy: FREE < PRO < PREMIUM = ADMIN (unlimited)
 */
import { TIER, Tier } from './tier-constants';

// ─── Limit constants ─────────────────────────────────────────────────────────

/** Maximum ZIP codes for FREE and PRO tiers (hard cap for unlimited tiers). */
export const MAX_ZIP_CODES_FREE = 15;
export const MAX_ZIP_CODES_PRO = 20;
/** Absolute maximum used by PREMIUM and ADMIN. Also exported from zoneUtils — keep in sync. */
export const MAX_ZIP_CODES_UNLIMITED = 200;

export const MAX_SUBSCRIPTIONS_FREE = 1;
export const MAX_SUBSCRIPTIONS_PRO = 3;
export const MAX_SUBSCRIPTIONS_PREMIUM = 10;
export const MAX_SUBSCRIPTIONS_ADMIN = 30;

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** True for PREMIUM and ADMIN — tiers that have no practical ZIP / subscription limits. */
export function isUnlimitedTier(tier: string | undefined, isAdmin?: boolean): boolean {
  return isAdmin === true
    || tier === TIER.PREMIUM
    || tier === TIER.ADMIN;
}

/** True for PRO, PREMIUM, and ADMIN — tiers with access to advanced filters. */
export function hasAdvancedFilters(tier: string | undefined, isAdmin?: boolean): boolean {
  return isAdmin === true
    || tier === TIER.PRO
    || tier === TIER.PREMIUM
    || tier === TIER.ADMIN;
}

/** Maximum number of ZIP codes allowed for the given tier. */
export function getMaxZipCodes(tier: string | undefined, isAdmin?: boolean): number {
  if (isUnlimitedTier(tier, isAdmin)) return MAX_ZIP_CODES_UNLIMITED;
  if (tier === TIER.PRO) return MAX_ZIP_CODES_PRO;
  return MAX_ZIP_CODES_FREE;
}

/** Maximum number of active subscriptions allowed for the given tier. */
export function getMaxSubscriptions(tier: string | undefined, isAdmin?: boolean): number {
  if (isAdmin === true || tier === TIER.ADMIN) return MAX_SUBSCRIPTIONS_ADMIN;
  if (tier === TIER.PREMIUM) return MAX_SUBSCRIPTIONS_PREMIUM;
  if (tier === TIER.PRO) return MAX_SUBSCRIPTIONS_PRO;
  return MAX_SUBSCRIPTIONS_FREE;
}

/**
 * Determines whether a user object represents an admin.
 * Checks both `isAdmin` flag and tier string — whichever is present.
 */
export function isAdminUser(user: { isAdmin?: boolean; tier?: Tier | string } | null | undefined): boolean {
  return user?.isAdmin === true || user?.tier === TIER.ADMIN;
}
